<section class="run-comparison" aria-labelledby="url-run-comparison-heading">
  <h3 id="url-run-comparison-heading" class="run-comparison-heading">Run comparison</h3>

  <p class="run-comparison-note" *ngIf="loading">Loading captured crawl runs…</p>
  <p class="run-comparison-note run-comparison-note--error" *ngIf="error">{{ error }}</p>

  <ng-container *ngIf="!loading && !error && payload">
    <p class="run-comparison-note" *ngIf="!hasRuns()">
      No crawl run has been captured for this domain yet, so there is nothing to compare.
    </p>

    <ng-container *ngIf="hasRuns() && comparison() as cmp">
      <p class="run-comparison-headline" [ngClass]="'run-comparison-headline--' + cmp.state">{{ cmp.headline }}</p>

      <p class="run-comparison-pair">
        <span class="run-comparison-pair-item">
          This run: <strong>{{ runLabel(cmp.currentRun) }}</strong>
          <span class="run-comparison-qualifier" *ngIf="runQualifier(cmp.currentRun) as q">({{ q }})</span>
        </span>
        <span class="run-comparison-pair-item" *ngIf="cmp.previousRun">
          Compared with: <strong>{{ runLabel(cmp.previousRun) }}</strong>
          <span class="run-comparison-qualifier" *ngIf="runQualifier(cmp.previousRun) as q">({{ q }})</span>
        </span>
        <span class="run-comparison-pair-item" *ngIf="!cmp.previousRun">
          No earlier run is paired to it.
        </span>
      </p>

      <ul class="run-comparison-notes" *ngIf="cmp.notes.length">
        <li *ngFor="let note of cmp.notes">{{ note }}</li>
      </ul>

      <div class="run-comparison-groups">
        <div class="run-comparison-group" *ngFor="let group of metricGroups()">
          <h4 class="run-comparison-group-heading">{{ group.label }}</h4>
          <dl class="run-metrics">
            <ng-container *ngFor="let metric of group.metrics">
              <dt class="run-metric-label">{{ metric.label }}</dt>
              <dd class="run-metric-value" [ngClass]="metricStateClass(metric)" [attr.title]="metric.reason">
                {{ metric.display }}
              </dd>
            </ng-container>
          </dl>
        </div>
      </div>

      <!--
        Issues resolved vs newly appeared (phase 4). Sits below the metric
        groups because it explains the `Critical issues` / `Warnings` counts
        directly above it — and sometimes contradicts what they imply, when a
        page resolves one issue and acquires another in the same run.
      -->
      <div class="run-issues" *ngIf="issues() as issues">
        <h4 class="run-comparison-group-heading">Issues</h4>

        <p class="run-issues-summary" [ngClass]="'run-issues-summary--' + issues.state">{{ issues.display }}</p>

        <ng-container *ngIf="hasIssueLists() && issuesMoved()">
          <div class="run-issue-list" *ngIf="issues.resolved.length">
            <h5 class="run-issue-heading run-issue-heading--resolved">
              Resolved ({{ issues.resolvedCount }})
            </h5>
            <ul>
              <li *ngFor="let issue of issues.resolved">
                <span class="run-issue-severity" [ngClass]="'run-issue-severity--' + issue.severity">
                  {{ issue.severity }}
                </span>
                {{ issueLabel(issue) }}
              </li>
            </ul>
          </div>

          <div class="run-issue-list" *ngIf="issues.appeared.length">
            <h5 class="run-issue-heading run-issue-heading--appeared">
              New in this run ({{ issues.appearedCount }})
            </h5>
            <ul>
              <li *ngFor="let issue of issues.appeared">
                <span class="run-issue-severity" [ngClass]="'run-issue-severity--' + issue.severity">
                  {{ issue.severity }}
                </span>
                {{ issueLabel(issue) }}
              </li>
            </ul>
          </div>

          <!--
            Neither resolved nor new: the same issue, re-graded. Called out
            separately because it is a change we made, not one the page made,
            and folding it into the other two lists would read as progress.
          -->
          <div class="run-issue-list" *ngIf="issues.reclassified.length">
            <h5 class="run-issue-heading run-issue-heading--reclassified">
              Reclassified ({{ issues.reclassifiedCount }})
            </h5>
            <ul>
              <li *ngFor="let issue of issues.reclassified">
                <span class="run-issue-severity" [ngClass]="'run-issue-severity--' + issue.to">{{ issue.to }}</span>
                {{ issueLabel(issue) }}
                <span class="run-issue-detail">was a {{ issue.from }}</span>
              </li>
            </ul>
          </div>
        </ng-container>

        <details class="run-issue-open" *ngIf="hasIssueLists() && issues.persisting.length">
          <summary>Still open ({{ issues.persistingCount }})</summary>
          <ul>
            <li *ngFor="let issue of issues.persisting">
              <span class="run-issue-severity" [ngClass]="'run-issue-severity--' + issue.severity">
                {{ issue.severity }}
              </span>
              {{ issueLabel(issue) }}
              <span class="run-issue-detail" *ngIf="issueReworded(issue) as reworded">{{ reworded }}</span>
            </li>
          </ul>
        </details>
      </div>

      <details class="run-comparison-history">
        <summary>Captured runs ({{ payload.runsCaptured }})</summary>
        <ul class="run-comparison-history-list">
          <li *ngFor="let entry of payload.history">
            <span class="run-comparison-history-date">{{ runLabel(entry.run) }}</span>
            <span class="run-comparison-history-state">{{ historyRowLabel(entry) }}</span>
            <span class="run-comparison-qualifier" *ngIf="runQualifier(entry.run) as q">{{ q }}</span>
          </li>
        </ul>
      </details>
    </ng-container>
  </ng-container>
</section>
